import { getPlatform } from "@memorang/helpers/platform";
import type { BaseItemSchema } from "@memorang/types";
import type { TrackAnswerEventRequest } from "../api/types";
import type { SessionState } from "../hooks/types";
import { getResponseTimesOut } from "./response-timing";
import { buildSectionDurationsMetadata } from "./section-timing";
import { getTimeOnItemSeconds } from "./time-on-item";

// Answer event sent when the user pauses or saves & exits: persists the
// session-level and item-level timers so the Resume Session API can restore
// both countdowns.
export const buildSessionTimeAnswerEventRequest = ({
	item,
	sessionState,
	projectId,
	organizationId,
	sessionTime,
}: {
	item: BaseItemSchema;
	sessionState: SessionState;
	projectId: number;
	organizationId: number;
	sessionTime: number;
}): TrackAnswerEventRequest => {
	const itemId = item.id!;
	const blockState = sessionState.blockStates.get(
		sessionState.currentBlockIndex,
	);
	const marked =
		item.answer?.marked ||
		Boolean(blockState?.markedItemsForReview?.includes(itemId));
	const highlight =
		item.answer?.highlight || blockState?.highlightedItems?.get(itemId) || "";

	const timeOnItemSeconds =
		getTimeOnItemSeconds(sessionState.itemViewedTimestamps, itemId) ?? 0;

	const timeElapsed = getResponseTimesOut(item.response, true)
		? sessionState.itemTimeElapsed
		: timeOnItemSeconds;

	return {
		itemId,
		itemVersionId: item.versionId || 0,
		sessionId: sessionState.id,
		collectionId: sessionState.collectionId,
		projectId,
		organizationId,
		sessionType: "SUMMATIVE",
		itemDetails: {
			type: "ANSWER_EVENT",
			itemType: item.metadata?.type!,
			variant: item.metadata?.type!,
		},
		response: {
			selectedChoiceIds:
				item.answer?.selectedChoiceIds ||
				sessionState.itemStates?.get(itemId)?.selectedChoiceIds ||
				[],
		},
		performance: {
			timeElapsed,
		},
		settings: {},
		userInteraction: {
			highlight,
			marked,
			sessionTime,
		},
		platform: getPlatform(),
		metadata: buildSectionDurationsMetadata(sessionState.sectionElapsedSeconds),
	};
};
